import { useState } from 'react';
import {
  Card, CardContent, Box, Typography, Chip, IconButton,
  Tooltip, Collapse,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';

export interface Donor {
  id: string;
  full_name: string;
  blood_type: string;
  governorate: string;
  city: string;
  phone?: string;
  is_available: boolean;
  last_donation: string | null;
}

interface Props {
  donor: Donor;
}

export default function DonorCard({ donor }: Props) {
  const { t } = useTranslation();
  const [showPhone, setShowPhone] = useState(false);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Blood type badge */}
          <Box
            sx={{
              minWidth: 56,
              height: 56,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #c62828 0%, #7f0f0f 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" fontWeight={900} color="white">
              {donor.blood_type}
            </Typography>
          </Box>

          {/* Info */}
          <Box flex={1} minWidth={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {donor.full_name}
              </Typography>
              <Chip
                size="small"
                icon={donor.is_available ? <CheckCircleIcon /> : <CancelIcon />}
                label={t(donor.is_available ? 'search.available' : 'search.unavailable')}
                color={donor.is_available ? 'success' : 'default'}
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              📍 {donor.governorate} — {donor.city}
            </Typography>

            {donor.last_donation && (
              <Typography variant="body2" color="text.secondary" mt={0.25}>
                🩸 {t('search.last_donation')}: {donor.last_donation}
              </Typography>
            )}

            {/* Phone — revealed on click */}
            <Collapse in={showPhone}>
              {donor.phone ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    📞 {donor.phone}
                  </Typography>
                  <Tooltip title="WhatsApp">
                    <IconButton
                      component="a"
                      href={`https://wa.me/${donor.phone.replace('+', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ color: '#25D366' }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" mt={0.5}>—</Typography>
              )}
            </Collapse>
          </Box>

          {/* Phone reveal button */}
          <Tooltip title={showPhone ? '' : '📞'}>
            <IconButton
              color="primary"
              onClick={() => setShowPhone((p) => !p)}
              size="small"
            >
              <PhoneIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}
